import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'

import request from '@/modules/base/util/request'
import { Formik, Form, useField, ErrorMessage } from 'formik'
import * as Yup from 'yup'

import appsConfig from '@/modules/apps/config'
import lang from '@/lang'

import { TextInput } from '@/modules/base/components/inputs'
import { ButtonIconSquare } from '@/modules/base/components/buttons'

import CheckIcon from '@assets/images/icons/check.svg'

import { toast } from '@/modules/base/components/toast/toastConfig'

const Input = ({ className, name, ...props }) => {
  const classes = useStyles()

  const [field] = useField({ name, ...props })
  return (
    <span className={classes.domainInputWrapper}>
      <input className={classes.domainInput} {...field} />
      <ErrorMessage name={name} className={classes.error} component="span" />
    </span>
  )
}

const CredentialForm = ({ setShowCredentialInput, appId }) => {
  const classes = useStyles()

  const validationSchema = Yup.object({
    aesMiteDomain: Yup.string().required(lang.emptyRequiredFieldsErrorMessage),
    aesMiteApikey: Yup.string().required(lang.emptyRequiredFieldsErrorMessage),
  })

  const onSubmit = async values => {
    try {
      await request(appsConfig.api.ownership(appId), { data: { customData: values }, auth: true, isConcurrent: true, method: 'PUT' })
      setShowCredentialInput(false)
    } catch (e) {
      toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
    }
  }

  const AccountLink = domain => `https://${domain || lang.mite.credentials.domain.default}.mite.de/myself`

  return (
    <Formik
      initialValues={{
        aesMiteDomain: '',
        aesMiteApikey: '',
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values: { aesMiteDomain }, isValid }) => (
        <Form>
          <div className={classes.credentials}>
            <div className={classes.credentialRow}>
              <span className={classes.credentialNumber}>1</span>
              <div>
                <div><strong>{lang.mite.credentials.domain.heading}</strong></div>
                <p>
                  {lang.mite.credentials.domain.text}<br />
                  https://<Input name="aesMiteDomain" />.mite.de
                </p>
              </div>
            </div>
            <div className={classes.credentialRow}>
              <span className={classes.credentialNumber}>2</span>
              <div>
                <div><strong>{lang.mite.credentials.access.heading}</strong></div>
                <p>{lang.mite.credentials.access.text}<a target="_blank" href={AccountLink(aesMiteDomain)}>{AccountLink(aesMiteDomain)}</a></p>
              </div>
            </div>
            <div className={classes.credentialRow}>
              <span className={classes.credentialNumber}>3</span>
              <div>
                <div><strong>{lang.mite.credentials.key.heading}</strong></div>
                <p>{lang.mite.credentials.key.text}</p>
                <TextInput name="aesMiteApikey" useFormik variant="light" />
              </div>
            </div>
            <ButtonIconSquare type="submit" icon={CheckIcon} label={lang.mite.credentials.submit} disabled={!isValid} />
          </div>
        </Form>

      )}
    </Formik>
  )
}

const useStyles = makeStyles(theme => ({
  credentials: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 80,
  },
  credentialRow: {
    maxWidth: 500,
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: 20,
    '& p': {
      marginBottom: 0,
      marginTop: 10,
    },
  },
  credentialNumber: {
    flex: '0 0 40px',
    height: 40,
    borderRadius: 4,
    textAlign: 'center',
    lineHeight: '40px',
    color: '#fff',
    background: theme.primarySuperLight,
    marginRight: 20,
  },
  domainInputWrapper: {
    display: 'inline-flex',
    flexDirection: 'column',
  },
  domainInput: {
    width: 250,
    '&, &:focus': {
      borderLeft: 'none',
      borderTop: 'none',
      borderRight: 'none',
      borderBottomWidth: 1,
      outline: 0,
    },
  },
  error: {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: 12,
    color: theme.red,
    marginTop: 10,
  },
}))

CredentialForm.propTypes = {
  setShowCredentialInput: PropTypes.func.isRequired,
  appId: PropTypes.string.isRequired,
}

export default CredentialForm
